import { create } from 'zustand'
import { theme } from 'antd'
import { storage } from '../config/storage.config'

export type ThemeMode = 'light' | 'dark'

export type ThemeSkinTokens = {
  primary: string
  pageBg: string
  cardBg: string
  text: string
}

export type ThemePresetId =
  | 'default-blue'
  | 'deep-ocean'
  | 'forest-green'
  | 'warm-sand'
  | 'custom'

export type ThemePreference = {
  mode: ThemeMode
  activePreset: ThemePresetId
  customSkin: ThemeSkinTokens
}

interface ThemeStore extends ThemePreference {
  resolvedSkin: ThemeSkinTokens
  setMode: (mode: ThemeMode) => void
  setPreset: (preset: ThemePresetId) => void
  updateCustomSkin: (patch: Partial<ThemeSkinTokens>) => void
  resetTheme: () => void
  applyTheme: () => void
  toggle: () => void
}

const themeKeys = ['primary', 'pageBg', 'cardBg', 'text'] as const

const cssVariables: Record<keyof ThemeSkinTokens, string> = {
  primary: '--color-primary',
  pageBg: '--color-bg',
  cardBg: '--color-bg-container',
  text: '--color-text',
}

export const THEME_PRESETS: Record<Exclude<ThemePresetId, 'custom'>, ThemeSkinTokens> = {
  'default-blue': {
    primary: '#1677FF',
    pageBg: '#F5F5F5',
    cardBg: '#FFFFFF',
    text: '#1F1F1F',
  },
  'deep-ocean': {
    primary: '#1D4ED8',
    pageBg: '#EAF2FF',
    cardBg: '#FFFFFF',
    text: '#102A43',
  },
  'forest-green': {
    primary: '#2F855A',
    pageBg: '#EEF7F1',
    cardBg: '#FFFFFF',
    text: '#1F3D2D',
  },
  'warm-sand': {
    primary: '#B7791F',
    pageBg: '#FBF5EA',
    cardBg: '#FFFDF8',
    text: '#4A3728',
  },
}

export const DEFAULT_THEME_PREFERENCE: ThemePreference = {
  mode: 'light',
  activePreset: 'default-blue',
  customSkin: { ...THEME_PRESETS['default-blue'] },
}

export function isHexColor(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value)
}

function normalizeHexColor(value: string): string {
  return value.trim().toUpperCase()
}

function normalizeSkinTokens(tokens: ThemeSkinTokens): ThemeSkinTokens {
  return {
    primary: normalizeHexColor(tokens.primary),
    pageBg: normalizeHexColor(tokens.pageBg),
    cardBg: normalizeHexColor(tokens.cardBg),
    text: normalizeHexColor(tokens.text),
  }
}

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark'
}

function isThemePresetId(value: unknown): value is ThemePresetId {
  return (
    value === 'custom' ||
    value === 'default-blue' ||
    value === 'deep-ocean' ||
    value === 'forest-green' ||
    value === 'warm-sand'
  )
}

function isThemeSkinTokens(value: unknown): value is ThemeSkinTokens {
  if (!value || typeof value !== 'object') {
    return false
  }

  const tokens = value as Record<string, unknown>

  return themeKeys.every((key) => {
    const tokenValue = tokens[key]
    return typeof tokenValue === 'string' && isHexColor(tokenValue)
  })
}

function resolveSkin(preference: ThemePreference): ThemeSkinTokens {
  if (preference.activePreset === 'custom') {
    return normalizeSkinTokens(preference.customSkin)
  }

  return normalizeSkinTokens(THEME_PRESETS[preference.activePreset])
}

function resolveAppliedSkin(mode: ThemeMode, skin: ThemeSkinTokens): ThemeSkinTokens {
  if (mode === 'light') {
    return skin
  }

  return {
    primary: skin.primary,
    pageBg: '#141414',
    cardBg: '#1F1F1F',
    text: '#FFFFFFD9',
  }
}

function resolvePrimaryBackground(mode: ThemeMode, primary: string) {
  return theme.getDesignToken({
    algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: { colorPrimary: primary },
  }).colorPrimaryBg
}

function applyThemeToDocument(mode: ThemeMode, skin: ThemeSkinTokens) {
  if (typeof document === 'undefined') {
    return
  }

  document.documentElement.setAttribute('data-theme', mode)

  const appliedSkin = resolveAppliedSkin(mode, skin)
  for (const key of themeKeys) {
    document.documentElement.style.setProperty(cssVariables[key], appliedSkin[key])
  }
  document.documentElement.style.setProperty('--color-primary-bg', resolvePrimaryBackground(mode, appliedSkin.primary))
}

function restoreThemePreference(): ThemePreference {
  const stored = storage.theme.get()

  if (isThemeMode(stored)) {
    return {
      ...DEFAULT_THEME_PREFERENCE,
      mode: stored,
    }
  }

  if (!stored || typeof stored !== 'object') {
    return DEFAULT_THEME_PREFERENCE
  }

  const candidate = stored as Partial<ThemePreference>
  if (
    isThemeMode(candidate.mode) &&
    isThemePresetId(candidate.activePreset) &&
    isThemeSkinTokens(candidate.customSkin)
  ) {
    return {
      mode: candidate.mode,
      activePreset: candidate.activePreset,
      customSkin: normalizeSkinTokens(candidate.customSkin),
    }
  }

  return DEFAULT_THEME_PREFERENCE
}

const storedPreference = restoreThemePreference()
const storedSkin = resolveSkin(storedPreference)

applyThemeToDocument(storedPreference.mode, storedSkin)

export const useThemeStore = create<ThemeStore>((set, get) => ({
  ...storedPreference,
  resolvedSkin: storedSkin,
  setMode: (mode) => {
    set({ mode })
    get().applyTheme()
  },
  setPreset: (preset) => {
    const nextPreference: ThemePreference = {
      mode: get().mode,
      activePreset: preset,
      customSkin: get().customSkin,
    }

    set({
      activePreset: preset,
      resolvedSkin: resolveSkin(nextPreference),
    })
    get().applyTheme()
  },
  updateCustomSkin: (patch) => {
    const mergedSkin = normalizeSkinTokens({
      ...get().customSkin,
      ...patch,
    })

    set({
      activePreset: 'custom',
      customSkin: mergedSkin,
      resolvedSkin: mergedSkin,
    })
    get().applyTheme()
  },
  resetTheme: () => {
    set({
      ...DEFAULT_THEME_PREFERENCE,
      customSkin: { ...DEFAULT_THEME_PREFERENCE.customSkin },
      resolvedSkin: resolveSkin(DEFAULT_THEME_PREFERENCE),
    })
    get().applyTheme()
  },
  applyTheme: () => {
    const state = get()
    const nextPreference: ThemePreference = {
      mode: state.mode,
      activePreset: state.activePreset,
      customSkin: normalizeSkinTokens(state.customSkin),
    }
    const nextResolvedSkin = resolveSkin(nextPreference)

    applyThemeToDocument(nextPreference.mode, nextResolvedSkin)
    storage.theme.set(nextPreference)
    set({
      customSkin: nextPreference.customSkin,
      resolvedSkin: nextResolvedSkin,
    })
  },
  toggle: () => get().setMode(get().mode === 'light' ? 'dark' : 'light'),
}))
